import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, ExclamationCircleIcon, PrinterIcon, QrcodeIcon } from '@heroicons/react/outline'
import { normalizeCode } from './printJobs'

const OUTCOME_KEYS = {
  iframe: 'pending_prints.outcome.iframe',
  fallback: 'pending_prints.outcome.fallback',
  blocked: 'pending_prints.outcome.blocked',
  timeout: 'pending_prints.outcome.timeout',
}
const SUCCESS_OUTCOMES = ['iframe', 'fallback']
const OPEN_LABEL_OUTCOMES = ['blocked', 'timeout']
const KEEP_INPUT_KINDS = ['error', 'choosing']

const STYLES = {
  card: {
    borderRadius: 24,
    border: '1px solid #E6E8F2',
    boxShadow: '0 1px 3px rgba(15,19,48,0.08), 0 1px 2px rgba(15,19,48,0.04)',
    padding: '26px 26px 22px',
  },
  titleRow: { gap: 10, marginBottom: 6 },
  icon: { width: 20, height: 20, color: '#1F39FF' },
  title: { fontSize: 15, fontWeight: 700, color: '#07091A' },
  subtitle: { fontSize: 13, color: '#5A608A', marginBottom: 16 },
  form: { gap: 10 },
  input: {
    height: 47,
    fontSize: 16,
    letterSpacing: '.09em',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D8DAE8',
    padding: '13px 15px',
    color: '#07091A',
  },
  inputFocus: { borderColor: '#6B7DF6', boxShadow: '0 0 0 3px rgba(31,57,255,0.30)' },
  button: { height: 47, width: 150, whiteSpace: 'nowrap', borderRadius: 12, background: '#1F39FF', fontSize: 14, fontWeight: 600, gap: 8 },
  buttonIcon: { width: 17, height: 17 },
  status: { marginTop: 14, padding: '11px 14px', borderRadius: 8, gap: 9, fontSize: 13 },
  error: { background: '#FBE1E5', color: '#B11829', fontWeight: 500 },
  success: { background: '#DCF5E6', color: '#0E7C4A', fontWeight: 600 },
  statusIcon: { width: 16, height: 16 },
}

const StatusLine = ({ status, onOpenLabel }) => {
  const { t } = useTranslation()
  // 'choosing': the store dialog is open; the line stays empty until there is an outcome.
  if (!status || status.kind === 'choosing') return null

  const printing = status.kind === 'printing'
  const isError = status.kind === 'error' || (!printing && !SUCCESS_OUTCOMES.includes(status.outcome))
  const outcomeMessage = OUTCOME_KEYS[status.outcome] ? t(OUTCOME_KEYS[status.outcome], { code: status.code }) : t('pending_prints.errors.generic')
  const message = status.kind === 'error' ? status.message : printing ? t('pending_prints.scan.printing') : outcomeMessage
  const showCopies = status.kind !== 'error' && status.copies > 1
  const showOpenLabel = status.kind === 'printed' && OPEN_LABEL_OUTCOMES.includes(status.outcome)
  const Icon = isError ? ExclamationCircleIcon : printing ? PrinterIcon : CheckCircleIcon

  return (
    <div role={isError ? 'alert' : 'status'} className="flex items-start" style={{ ...STYLES.status, ...(isError ? STYLES.error : STYLES.success) }}>
      <Icon className="flex-shrink-0" style={STYLES.statusIcon} aria-hidden="true" />
      <div className="flex flex-col">
        <span>{message}</span>
        {showCopies && <span>{t('pending_prints.outcome.copies', { count: status.copies })}</span>}
        {showOpenLabel && (
          <button type="button" className="self-start underline" onClick={onOpenLabel}>
            {t('pending_prints.outcome.open_label')}
          </button>
        )}
      </div>
    </div>
  )
}

export const PrintScanBar = ({
  onSubmit,
  onType = () => {},
  busy = false,
  disabled = false,
  status = null,
  onOpenLabel = () => {},
  inputRef: parentInputRef,
}) => {
  const { t } = useTranslation()
  const ownInputRef = useRef(null)
  const inputRef = parentInputRef || ownInputRef
  const wasBusyRef = useRef(busy)
  const statusRef = useRef(status)
  statusRef.current = status
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)

  const locked = busy || disabled
  const canSubmit = !locked && normalizeCode(value) !== null

  useEffect(() => {
    if (!disabled) inputRef.current?.focus()
    // Only on mount: later focus moves are the operator's or follow an outcome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (wasBusyRef.current && !busy) {
      // After an error the code stays, selected, so the next scanner read replaces it instead of appending.
      if (KEEP_INPUT_KINDS.includes(statusRef.current?.kind)) {
        inputRef.current?.focus()
        inputRef.current?.select()
      } else {
        setValue('')
        inputRef.current?.focus()
      }
    }
    wasBusyRef.current = busy
  }, [busy, inputRef])

  const submit = () => {
    if (canSubmit) onSubmit(value)
  }

  const handleChange = event => {
    setValue(event.target.value)
    onType()
  }

  const handleKeyDown = event => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    submit()
  }

  return (
    <section className="bg-white" style={STYLES.card}>
      <div className="flex items-center" style={STYLES.titleRow}>
        <QrcodeIcon style={STYLES.icon} aria-hidden="true" />
        <h2 style={STYLES.title}>{t('pending_prints.scan.title')}</h2>
      </div>
      <p style={STYLES.subtitle}>{t('pending_prints.scan.subtitle')}</p>
      <div className="flex" style={STYLES.form}>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 min-w-0 pending-prints-scan-input font-mono bg-white outline-none focus:outline-none disabled:opacity-50"
          style={{ ...STYLES.input, ...(focused ? STYLES.inputFocus : {}) }}
          placeholder={t('pending_prints.scan.placeholder')}
          aria-label={t('pending_prints.scan.placeholder')}
          value={value}
          disabled={locked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <button
          type="button"
          className="flex flex-shrink-0 items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={STYLES.button}
          disabled={!canSubmit}
          onClick={submit}
        >
          <PrinterIcon style={STYLES.buttonIcon} aria-hidden="true" />
          {busy ? t('pending_prints.scan.printing') : t('pending_prints.scan.submit')}
        </button>
      </div>
      <StatusLine status={status} onOpenLabel={onOpenLabel} />
    </section>
  )
}
